import React, { useEffect, useState, useContext } from "react";
import SEO from "./seo";
import { Box, Button, Flex } from "rebass/styled-components";
import Axios from "axios";
import GoogleMap from "./googleMap";
import Text from "./text";
import { ThemeProvider } from "styled-components";
import { avicennaTheme } from "../theme/GlobalStyle";
import { ServicesContext } from "../contexts/ServicesContext";
import PharmacySearch from "./pharmacySearch";
import { Pharmacy } from "@avicennapharmacy/managemymeds-shared";
import useCurrentLocation from "./hooks/useCurrentLocation";

const defaultLatLng = {
  latitude: 55.02602,
  longitude: -1.62762,
};

type inputProps = {
  labelColour: string | string[] | null;
  pharmacy: string | null;
  owned: string | string[] | null;
  showSearch: boolean | null;
};

const ServicesMap = ({ labelColour, pharmacy, owned, showSearch }: inputProps) => {
  const [allPharmacies, setAllPharmacies] = useState<any[]>([]);
  const [mapMarkers, setMapMarkers] = useState<any[]>([]);
  const [error, setError] = useState<string>("");
  const [location, locationLoading, locationError] = useCurrentLocation();
  const [loading, setLoading] = useState(false);
  const {
    setSelectedPharmacyApiKey,
    selectedPharmacyId,
    setSelectedPharmacyId,
  } = useContext<any>(ServicesContext);

  labelColour = labelColour ?? "black";

  const getAllPharmacies = async (latlng: {}) => {
    const { data } = await Axios.post(
      process.env.GATSBY_PHARMACY_SEARCH_ENDPOINT!,
      {
        postcode: '',
        tag: '',
        distance: 1000, // capture all pharmacies on the map for now.
        ...latlng,
      }
    );
    return data.pharmacies;
  };

  const getPharmacyGroup = async (pharmacyId: string) => {
    const { data } = await Axios.get(
      `${process.env.GATSBY_PHARMACY_GROUP_ENDPOINT!}/${pharmacyId}`
    );
    return data;
  };

  const addMapMarkers = async () => {
    let pharmacies = [];
    try {
      pharmacies = await getAllPharmacies(
        Object.keys(location).length ? location : defaultLatLng
      );
    } catch (error) {
      // If the patient's postcode could not be found, try again with a known postcode.
      if (error.response.status === 422) {
        try {
          pharmacies = await getAllPharmacies(defaultLatLng);
        } catch (innerError) {
          pharmacies = [];
        }
      }
    }

    pharmacies = pharmacies.filter((p: any) => {
      return (
        p.isServiceBookingEnabled === true
        && (owned === "true" ? p.ownershipType === "Owned" : true)
      );
    });

    const markers = pharmacies.map(
      ({
        pharmacyId,
        latitude,
        longitude,
        businessName,
        address1,
        address2,
        address3,
        city,
        postcode,
      }: any) => ({
        key: pharmacyId,
        lat: latitude!,
        lng: longitude!,
        info: [businessName, address1, address2, address3, city, postcode]
          .filter(Boolean)
          .join("\n"),
      })
    );

    setAllPharmacies(pharmacies.map((p: any) => ({ ...p, id: p.pharmacyId })));
    setMapMarkers(markers);
  };

  const onPharmacySelect = async (pharmacy: Pharmacy) => {
    try {
      const group = await getPharmacyGroup(pharmacy.id);
      await setSelectedPharmacyApiKey(group.bookingApiKey);
    } catch {
      setError("There was an error loading services");
    }
    setSelectedPharmacyId(pharmacy.id);
  };

  useEffect(() => {
    addMapMarkers();
    locationLoading && locationError.message.length === 0
      ? setLoading(true)
      : setLoading(false);
  }, [location, locationError]);

  useEffect(() => {
    if (pharmacy) {
      const initialisePharmacy = async () => {
        try {
          const group = await getPharmacyGroup(pharmacy);
          await setSelectedPharmacyApiKey(group.bookingApiKey);
        } catch {
          setError("There was an error loading services");
        }
        setSelectedPharmacyId(pharmacy);
      }
      initialisePharmacy();
    }
  }, [])

  return (
    <ThemeProvider theme={avicennaTheme}>
      <SEO title="Book a Pharmacy Service" />
      <Flex flexWrap="wrap">
        <Box width={1}>
          {error.trim() !== "" && (
            <Box width={1} pr={3} backgroundColor="errorLight" mb={3}>
              <Text color="errorDark" p={3}>
                {error}
              </Text>
            </Box>
          )}
          {showSearch && (
            <Box width={1} mb={3}>
              <PharmacySearch
                loading={loading}
                select={async (newPharmacy: Pharmacy) =>
                  await onPharmacySelect(newPharmacy)
                }
                allPharmacies={allPharmacies}
                owned={owned}
              />
            </Box>
          )}
          <Box width={1}>
            <GoogleMap
              markers={mapMarkers}
              labelColour={labelColour}
              selectedMarker={mapMarkers.find(
                ({ key }) => selectedPharmacyId === key
              )}
              onMarkerClick={async (markerKey: string) => {
                try {
                  const group = await getPharmacyGroup(markerKey);
                  setSelectedPharmacyApiKey(group.bookingApiKey);
                  setSelectedPharmacyId(markerKey);
                } catch {
                  setError("There was an error loading services");
                }
              }}
            />
          </Box>
        </Box>
      </Flex>
    </ThemeProvider>
  );
};

export default ServicesMap;
